import { isNull, uniq } from 'lodash/fp';
import React, { useCallback, useEffect, useMemo } from 'react';

import { useAppDispatch, useAppSelector } from '../../../../hooks/react-redux';
import { useFetchStarted } from '../../../../hooks/useFetchStarted';
import { Button } from '../../../shared/button/Button';
import { SpaceBetweenButtonWrapper } from '../../../shared/button/SpaceBetweenButtonWrapper';
import { Agency } from '../../../shared/icons';
import { SidebarMenuModal } from '../../../shared/modal/SidebarMenuModal';
import { SidebarMenuOption } from '../../../shared/sidebar-menu/SidebarMenu';
import { fetchAllDocumentNamesStarted, getAllDocumentNames } from '../../documents/store';
import { fetchAllDropdownListsStarted } from '../../dropdown-lists/store';
import { ClientFeaturePermission, fetchAvailableFeaturePermissionsStarted } from '../../users/store';
import styles from '../Clients.module.scss';
import { Client, ClientOpinion, ClientSection, fetchClientFeaturePermissionsStarted, fetchRejectedQuestionsStarted, forgiveRejectedQuestionsStarted, getAvailableClientFeaturePermissions, getFilesToUpload, getIsFetchingRejectedQuestions, getIsUploading, getOpenClientSection, getRejectedQuestionsToForgive, getSelectedClientFewShotAgreementTypeId, setClientSelectedFewShotAgreementTypeId, setOpenClientSection, updateClientDetails, updateClientStarted, uploadDocumentStarted } from '../store';
import { ClientDocuments } from './ClientDocuments';
import { ClientFeaturePermissions } from './ClientFeaturePermissions';
import { ClientGeneral } from './ClientGeneral';
import { ClientOpinions } from './ClientOpinions';
import { ClientUsers } from './ClientUsers';
import { ClientTermsAndConditions } from './ClientTermsAndConditions';
import { RejectedChatBotQuestions } from './RejectedChatBotQuestions';
import { fetchAllRolesStarted } from '../../roles/store';
import { ClientFewShotDocuments } from './ClientFewShotDocuments';
import { getPriorityOrderHasUpdated, updateFewShotPriorityOrderStarted } from '../../ai/few-shot/store';

interface ClientModalProps {
    isOpen: boolean;
    closeModal: () => void;
    client: Client;
}

export const ClientModal: React.FC<ClientModalProps> = ({
    isOpen,
    closeModal,
    client
}) => {
    const { clientName, description, users, clientDatasetIds, clientFeaturePermissionIds, clientId, clientOpinions, clientTermsAndConditionsDocuments } = client;
    const dispatch = useAppDispatch();
    const selectedSection = useAppSelector(getOpenClientSection);
    const allDocumentNames = useAppSelector(getAllDocumentNames);
    const allClientFeaturePermissions = useAppSelector(getAvailableClientFeaturePermissions);
    const termsAndConditionsFiles = useAppSelector(getFilesToUpload);
    const isUploading = useAppSelector(getIsUploading);
    const rejectedQuestionsToForgive = useAppSelector(getRejectedQuestionsToForgive);
    const isFetchingRejectedQuestions = useAppSelector(getIsFetchingRejectedQuestions);
    const fewShotPriorityHasUpdated = useAppSelector(getPriorityOrderHasUpdated);
    const selectedFewShotAgreementTypeId = useAppSelector(getSelectedClientFewShotAgreementTypeId);

    useFetchStarted([fetchAllDocumentNamesStarted(), fetchAvailableFeaturePermissionsStarted(), fetchClientFeaturePermissionsStarted(), fetchAllDropdownListsStarted(), fetchRejectedQuestionsStarted()]);
    useEffect(() => { dispatch(fetchAllRolesStarted(clientId)); }, [dispatch, clientId]);

    const datasetOptions = useMemo(() => allDocumentNames.filter(({ datasetId }) => datasetId), [allDocumentNames]);
    const permissionOptions = useMemo(() => allClientFeaturePermissions.filter(({ label }) => label).sort((a, b) => a.label.localeCompare(b.label)), [allClientFeaturePermissions]);

    const clientHasChatBotPermission = useMemo(() => clientFeaturePermissionIds?.includes(ClientFeaturePermission.CUSTOM_QUERYING_DOCUMENTS_WITH_AI), [clientFeaturePermissionIds]);

    const updateClientProperties = useCallback((key: keyof Client, value: string | number[] | ClientOpinion[] | number | null) => dispatch(updateClientDetails(key, value)), [dispatch]);

    const confirm = useCallback(() => dispatch(updateClientStarted()), [dispatch]);

    const deselectFewShotAgreementTypeId = useCallback(() => dispatch(setClientSelectedFewShotAgreementTypeId(null)), [dispatch]);
    const saveFewShotPriorityOrder = useCallback(() => dispatch(updateFewShotPriorityOrderStarted()), [dispatch]);

    useEffect(() => {
        if (!clientId) {
            const newClientDatasetIds: number[] = uniq(datasetOptions.map(({ datasetId }) => datasetId!));
            updateClientProperties('clientOpinions', []);
            updateClientProperties('clientDatasetIds', newClientDatasetIds);
        }
        if (clientId && !clientFeaturePermissionIds) {
            updateClientProperties('clientFeaturePermissionIds', []);
        }
    }, [clientId, datasetOptions, updateClientProperties, permissionOptions, clientFeaturePermissionIds]);

    useEffect(() => () => {
        dispatch(setOpenClientSection(ClientSection.GENERAL));
    }, [dispatch]);

    const testId = 'upsert-client';
    const selectSection = useCallback((sectionId: string) => dispatch(setOpenClientSection(sectionId as ClientSection)), [dispatch]);

    const upload = useCallback(() => dispatch(uploadDocumentStarted()), [dispatch]);

    const forgiveSelectedRejectedQuestions = useCallback(() => dispatch(forgiveRejectedQuestionsStarted(rejectedQuestionsToForgive)), [dispatch, rejectedQuestionsToForgive]);

    const sections: SidebarMenuOption[] = useMemo(() => {
        const sections = [
            { id: ClientSection.GENERAL, label: ClientSection.GENERAL, element: <ClientGeneral client={client} /> },
            { id: ClientSection.USERS, label: ClientSection.USERS, element: <ClientUsers users={users} clientId={clientId} clientName={clientName} /> },
            { id: ClientSection.FEATURE_PERMISSIONS, label: ClientSection.FEATURE_PERMISSIONS, element: <ClientFeaturePermissions clientFeaturePermissionIds={clientFeaturePermissionIds} /> },
            { id: ClientSection.DATASETS, label: ClientSection.DATASETS, element: <ClientDocuments clientDatasetIds={clientDatasetIds} /> },
            { id: ClientSection.OPINIONS, label: ClientSection.OPINIONS, element: <ClientOpinions clientOpinions={clientOpinions} /> },
            { id: ClientSection.TERMS_AND_CONDITIONS, label: ClientSection.TERMS_AND_CONDITIONS, element: <ClientTermsAndConditions clientTermsAndConditionsDocuments={clientTermsAndConditionsDocuments} clientId={clientId} /> }
        ];
        if (clientHasChatBotPermission) {
            sections.splice(5, 0, { id: ClientSection.REJECTED_QUESTIONS, label: ClientSection.REJECTED_QUESTIONS, element: <RejectedChatBotQuestions /> });
        }
        if (client.fewShotAgreementTypeIds.length > 0) {
            sections.push({ id: ClientSection.FEW_SHOT_DOCUMENTS, label: ClientSection.FEW_SHOT_DOCUMENTS, element: <ClientFewShotDocuments agreementTypeIds={client.fewShotAgreementTypeIds} /> });
        }
        return sections;
    }, [client, users, clientId, clientName, clientFeaturePermissionIds, clientDatasetIds, clientOpinions, clientTermsAndConditionsDocuments, clientHasChatBotPermission]);

    const confirmLabel = useMemo(() => clientId ? 'Update' : 'Create', [clientId]);
    const confirmDisabled = useMemo(() => !clientName || !description, [clientName, description]);

    const buttonWrapper = useMemo(() => {
        let rightButtons: JSX.Element[] = [];
        if ([ClientSection.GENERAL, ClientSection.FEATURE_PERMISSIONS, ClientSection.DATASETS, ClientSection.OPINIONS].includes(selectedSection)) {
            rightButtons = [<Button onClick={confirm} label={confirmLabel} disabled={confirmDisabled} key='confirm' />];
        }
        if (ClientSection.FEW_SHOT_DOCUMENTS === selectedSection) {
            rightButtons = [<Button onClick={saveFewShotPriorityOrder} label='Save' disabled={!fewShotPriorityHasUpdated} key='confirm' />];
            if (!isNull(selectedFewShotAgreementTypeId)) {
                rightButtons.unshift(<Button onClick={deselectFewShotAgreementTypeId} label='Agreement Types' key='agreement types' />);
            }
        }
        if (selectedSection === ClientSection.TERMS_AND_CONDITIONS && termsAndConditionsFiles.length > 0) {
            rightButtons = [<Button onClick={upload} label='Upload' disabled={isUploading} key='upload' />];
        }
        if (selectedSection === ClientSection.REJECTED_QUESTIONS && rejectedQuestionsToForgive.length > 0) {
            rightButtons = [<Button onClick={forgiveSelectedRejectedQuestions} label='Forgive Selected' disabled={isFetchingRejectedQuestions} key='forgiveRejectedQuestions' />];
        }
        return (
            <SpaceBetweenButtonWrapper leftButtons={[<Button onClick={closeModal} label='Close' key='close' />]} rightButtons={rightButtons} />
        );
    }, [closeModal, confirm, confirmLabel, confirmDisabled, selectedSection, termsAndConditionsFiles, isUploading, upload, rejectedQuestionsToForgive, isFetchingRejectedQuestions, forgiveSelectedRejectedQuestions, fewShotPriorityHasUpdated, saveFewShotPriorityOrder, selectedFewShotAgreementTypeId, deselectFewShotAgreementTypeId]);

    return (
        <SidebarMenuModal
            isOpen={isOpen}
            sections={sections}
            selected={selectedSection}
            openSection={selectSection}
            testId={testId}
            menuDisabled={false}
            buttonWrapper={buttonWrapper}
            headerLabel='Client'
            headerIcon={Agency}
            wrapperClassName={styles.clientWrapper}
            sidebarWidth={170}
        />
    );
};
